import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const InstallationPage = () => {
  const { scrollY } = useScroll();

  const heroY = useTransform(scrollY, [0, 500], [0, -120]);
  const fadeIn = useTransform(scrollY, [0, 300], [1, 0.6]);

  const projects = [
    {
      title: "System Overview",
      image: "/Assets/installation1.jpeg",
      tag: "Overview",
      result: "System architecture & setup",
      link: "/case-study/lagos-installation#overview",
    },
    {
      title: "Installation Process",
      image: "/Assets/installation2.jpeg",
      tag: "Process",
      result: "Step-by-step deployment",
      link: "/case-study/lagos-installation#process",
    },
    {
      title: "Final Result",
      image: "/Assets/installation4.jpeg",
      tag: "Outcome",
      result: "Performance & results",
      link: "/case-study/lagos-installation#result",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Solar Installation Services in Lagos, Nigeria | Solaris Surge Energy
          Solutions
        </title>

        <meta
          name="description"
          content="Professional solar installation services in Lagos, Nigeria. We install solar panels, inverters, batteries and complete solar power systems for homes and businesses."
        />

        <meta
          property="og:title"
          content="Solar Installation Services in Lagos, Nigeria | Solaris Surge Energy Solutions"
        />

        <meta
          property="og:description"
          content="Professional solar installation services in Lagos, Nigeria. We install solar panels, inverters, batteries and complete solar power systems for homes and businesses."
        />

        <meta
          property="og:url"
          content="https://www.solarissurge.net/installations"
        />

        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Solar Installation",
            name: "Solar Installation Services",
            provider: {
              "@type": "LocalBusiness",
              name: "Solaris Surge Energy Solutions",
              url: "https://www.solarissurge.net",
              telephone: "+2348165213568",
            },
            areaServed: {
              "@type": "City",
              name: "Lagos",
            },
          })}
        </script>
      </Helmet>

      <Navbar />

      {/* ================= HERO ================= */}
      <section className="relative h-[60vh] overflow-hidden flex items-center justify-center text-white">
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            y: heroY,
            opacity: fadeIn,
            backgroundImage: "url('/Assets/solar-panel-installation.webp')",
          }}
        />

        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center max-w-3xl px-6"
        >
          <h1 className="text-4xl md:text-6xl font-extrabold">
            Solar Installation Services in Lagos, Nigeria
          </h1>
          <p className="mt-4 text-lg text-white/80">
            See how we transform homes and businesses with reliable solar
            systems across Lagos.
          </p>
        </motion.div>
      </section>

      {/* ================= SEO CONTENT ================= */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Professional Solar Installation Services Across Lagos
          </h2>

          <div className="space-y-6 text-lg leading-relaxed text-gray-700">
            <p>
              Solaris Surge Energy Solutions provides professional solar
              installation services throughout Lagos, Nigeria. We design and
              install complete solar power systems for homes, offices, schools,
              churches, and commercial facilities.
            </p>

            <p>
              Our team handles everything from solar panel installation and
              inverter setup to battery integration and system maintenance.
              Whether you need a small residential backup solution or a large
              commercial solar project, we provide reliable and affordable
              energy systems tailored to your energy needs.
            </p>

            <p>
              We proudly serve customers across Ojo, Alaba, Festac, Surulere,
              Ikeja, Lekki, Ajah, Victoria Island, and other areas of Lagos
              State, delivering dependable renewable energy solutions that
              reduce electricity costs and provide uninterrupted power.
            </p>
          </div>
        </div>
      </section>

      {/* ================= CASE STUDIES ================= */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              visible: { transition: { staggerChildren: 0.2 } },
            }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10"
          >
            {projects.map((project, index) => (
              <Link to={project.link} className="block" key={index}>
                <motion.div
                  className="group relative rounded-3xl overflow-hidden cursor-pointer"
                  initial="rest"
                  whileHover="hover"
                  animate="rest"
                >
                  {/* IMAGE */}
                  <motion.div
                    className="relative h-[300px] overflow-hidden"
                    variants={{
                      rest: { scale: 1 },
                      hover: { scale: 1.05 },
                    }}
                  >
                    <img
                      src={project.image}
                      alt="Residential solar installation project in Lagos Nigeria"
                      className="w-full h-full object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  </motion.div>

                  {/* CONTENT */}
                  <motion.div
                    className="absolute bottom-0 p-6 text-white"
                    variants={{
                      rest: { y: 0 },
                      hover: { y: -10 },
                    }}
                  >
                    <span className="text-xs uppercase tracking-wide text-green-300">
                      {project.tag}
                    </span>

                    <h3 className="text-xl font-bold mt-1">{project.title}</h3>

                    <p className="text-sm text-white/80 mt-1">
                      {project.result}
                    </p>

                    <motion.div
                      className="mt-3 text-sm font-medium text-green-300"
                      variants={{
                        hover: { x: 5 },
                      }}
                    >
                      View Case Study →
                    </motion.div>
                  </motion.div>

                  {/* glow border */}
                  <motion.div
                    className="absolute inset-0 rounded-3xl"
                    initial={{ opacity: 0 }}
                    whileHover={{
                      opacity: 1,
                      boxShadow: "0 0 40px rgba(34,197,94,0.3)",
                    }}
                  />
                </motion.div>
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="bg-gradient-to-b from-white to-green-50 text-gray-900 py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto px-6"
        >
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to Power Your Home?
          </h2>
          <p className="mt-4 text-black/80">
            Get a custom solar installation tailored to your needs.
          </p>

          <Link to="/contactsection">
            <motion.button
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              className="mt-6 bg-orange-500 px-8 py-3 rounded-full font-semibold shadow-lg"
            >
              Get Free Estimate
            </motion.button>
          </Link>
        </motion.div>
      </section>

      {/* WhatsApp */}
      <a
        href="https://wa.me/+2348165213568?text=Hi!"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 bg-green-500 p-3 rounded-full shadow-lg hover:bg-green-400"
      >
        <img src="/Assets/chat.png" alt="WhatsApp Chat" className="w-8 h-8" />
      </a>

      <Footer />
    </>
  );
};

export default InstallationPage;
